'use client';

import * as React from 'react';
import styles from './Alert.module.css';
import clsx from 'clsx';
import { Tag } from './Tag';

export interface AlertProps {
  title?: string;
  children: React.ReactNode;
  variant?: 'info' | 'success' | 'warning' | 'error';
  appearance?: 'card' | 'banner';
  dismissible?: boolean;
  onDismiss?: () => void;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

const variantTagColor: Record<string, 'info' | 'success' | 'warning' | 'danger'> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'danger',
};

export const Alert: React.FC<AlertProps> = ({
  title,
  children,
  variant = 'info',
  appearance = 'card',
  dismissible = false,
  onDismiss,
  action,
  className,
}) => {
  const icons: Record<string, React.ReactNode> = {
    info: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" strokeLinecap="round" />
      </svg>
    ),
    success: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M8 12l2.5 2.5L16 9" />
      </svg>
    ),
    warning: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 9v4" />
        <path d="M12 17h.01" strokeLinecap="round" />
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
    ),
    error: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M15 9l-6 6M9 9l6 6" />
      </svg>
    ),
  };

  return (
    <div
      className={clsx(styles.alert, styles[variant], className)}
      data-appearance={appearance}
      role="alert"
    >
      <div className={styles.icon}>{icons[variant]}</div>
      <div className={styles.content}>
        {title ? (
          <>
            <div className={styles.titleLine}>
              <Tag variant="signal" color={variantTagColor[variant]} size="sm">{title}</Tag>
            </div>
            <div className={styles.message}>{children}</div>
          </>
        ) : (
          <div className={styles.message}>{children}</div>
        )}
        {action && (
          <button className={styles.action} onClick={action.onClick}>
            {action.label}
          </button>
        )}
      </div>
      {dismissible && (
        <button className={styles.dismiss} onClick={onDismiss} aria-label="Dismiss">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
};
